import { Fragment, useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { Container, FadeUp } from "./primitives";
import {
  LayoutDashboard,
  Radar,
  UserSquare2,
  Kanban,
  CalendarClock,
  BarChart3,
  ShieldCheck,
  Users2,
} from "lucide-react";

const SHOTS = [
  {
    k: "Dashboard",
    d: "Your morning briefing — new leads, priority threads, team velocity.",
    icon: LayoutDashboard,
    render: <ShotDashboard />,
  },
  {
    k: "Lead Discovery",
    d: "AI-surfaced conversations that match your ICP, ranked by intent.",
    icon: Radar,
    render: <ShotDiscovery />,
  },
  {
    k: "Lead Detail",
    d: "A full picture of a prospect — signals, history, next best action.",
    icon: UserSquare2,
    render: <ShotLeadDetail />,
  },
  {
    k: "Pipeline",
    d: "Drag-and-drop CRM board that mirrors your real sales motion.",
    icon: Kanban,
    render: <ShotPipeline />,
  },
  {
    k: "Scheduler",
    d: "Plan replies, DMs and campaigns across time zones and accounts.",
    icon: CalendarClock,
    render: <ShotScheduler />,
  },
  {
    k: "Analytics",
    d: "Reply-rate, conversion, team velocity and campaign ROI.",
    icon: BarChart3,
    render: <ShotAnalytics />,
  },
  {
    k: "Approval Queue",
    d: "Every outbound reviewed. Brand-safe by default.",
    icon: ShieldCheck,
    render: <ShotApproval />,
  },
  {
    k: "Team Activity",
    d: "See what your team is doing — and coach in real time.",
    icon: Users2,
    render: <ShotTeam />,
  },
];

export default function Screenshots() {
  return (
    <section id="screenshots" data-testid="screenshots-section" className="relative py-32 md:py-40 border-t border-white/10">
      <Container>
        <FadeUp>
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-16">
            <div>
              <div className="font-mono-x text-xs tracking-[0.24em] text-white/40 uppercase">
                Chapter 05 · Product
              </div>
              <h2 className="mt-4 font-display text-[clamp(2.25rem,5vw,4.75rem)] leading-[0.98] tracking-[-0.03em] max-w-3xl">
                A product that feels{" "}
                <span className="font-serif-x italic">quiet</span>{" "}
                — and works loud.
              </h2>
            </div>
            <p className="max-w-md text-white/55 text-[15px] leading-relaxed">
              Every surface engineered around speed, clarity and craft. Explore eight of the pages your team will live in.
            </p>
          </div>
        </FadeUp>

        <div className="space-y-24 md:space-y-32">
          {SHOTS.map((s, i) => (
            <ShotBlock key={s.k} index={i} data={s} reverse={i % 2 === 1} />
          ))}
        </div>
      </Container>
    </section>
  );
}

function ShotBlock({ index, data, reverse }) {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start end", "end start"],
  });
  const y = useTransform(scrollYProgress, [0, 1], [40, -40]);

  return (
    <div ref={ref} className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-14 items-center">
      <FadeUp
        className={`lg:col-span-4 ${reverse ? "lg:order-2" : ""}`}
        delay={0.05}
      >
        <div className="flex flex-col gap-5">
          <div className="flex items-center gap-3">
            <span className="flex h-10 w-10 items-center justify-center rounded-xl border border-white/10 bg-white/[0.03]">
              <data.icon className="h-4 w-4 text-white/80" strokeWidth={1.5} />
            </span>
            <span className="font-mono-x text-[10px] tracking-[0.24em] uppercase text-white/40">
              {String(index + 1).padStart(2, "0")} / 08 · {data.k}
            </span>
          </div>
          <h3 className="font-display text-3xl md:text-5xl tracking-[-0.03em] leading-[1] text-white">
            {data.k}
          </h3>
          <p className="text-[15px] md:text-base text-white/60 leading-relaxed max-w-md">
            {data.d}
          </p>
        </div>
      </FadeUp>

      <div className={`lg:col-span-8 ${reverse ? "lg:order-1" : ""} relative`}>
        {/* Spotlight */}
        <div className="absolute -inset-10 pointer-events-none bg-[radial-gradient(circle_at_50%_50%,rgba(124,58,237,0.16),transparent_60%)]" />
        <motion.div style={{ y }} className="relative">
          <div className="relative rounded-[24px] overflow-hidden border border-white/10 bg-[#0a0a10] ring-glow">
            <div className="flex items-center gap-2 px-4 py-3 border-b border-white/10 bg-white/[0.02]">
              <div className="flex gap-1.5">
                <span className="h-2.5 w-2.5 rounded-full bg-[#ff5f57]" />
                <span className="h-2.5 w-2.5 rounded-full bg-[#febc2e]" />
                <span className="h-2.5 w-2.5 rounded-full bg-[#28c840]" />
              </div>
              <div className="mx-auto text-[11px] font-mono-x text-white/40 tracking-wider">
                app.sociallead.ai / {data.k.toLowerCase().replace(/ /g, "-")}
              </div>
            </div>
            <div className="relative">{data.render}</div>
          </div>
        </motion.div>
      </div>
    </div>
  );
}

/* ---------- Individual mock renders ---------- */

function ShotDashboard() {
  return (
    <div className="p-5 md:p-6 grid grid-cols-12 gap-3 min-h-[440px]">
      <div className="col-span-12 md:col-span-3 flex flex-col gap-2">
        {["Overview", "Leads", "Pipeline", "Threads", "Approvals", "Reports"].map((s, i) => (
          <div key={s} className={`text-[11px] font-mono-x px-3 py-2 rounded-lg border ${i===0 ? "bg-white/10 border-white/15 text-white" : "border-transparent text-white/45"}`}>{s.toLowerCase()}</div>
        ))}
      </div>
      <div className="col-span-12 md:col-span-9 flex flex-col gap-3">
        <div className="grid grid-cols-4 gap-2">
          {[{k:"1,248",l:"Leads"},{k:"312",l:"Threads"},{k:"94",l:"Approvals"},{k:"$41.2k",l:"Pipeline"}].map((it) => (
            <div key={it.l} className="rounded-xl border border-white/10 bg-white/[0.02] p-3">
              <div className="text-[10px] uppercase tracking-[0.18em] text-white/40 font-mono-x">{it.l}</div>
              <div className="mt-1 font-display text-xl">{it.k}</div>
            </div>
          ))}
        </div>
        <div className="rounded-xl border border-white/10 bg-white/[0.02] p-4 h-[180px] relative overflow-hidden">
          <div className="text-[10px] uppercase tracking-[0.18em] text-white/40 font-mono-x">Weekly Momentum</div>
          <svg viewBox="0 0 500 100" className="mt-2 w-full h-[130px]">
            <defs>
              <linearGradient id="dashg" x1="0" x2="0" y1="0" y2="1">
                <stop offset="0%" stopColor="#60a5fa" stopOpacity="0.6"/>
                <stop offset="100%" stopColor="#60a5fa" stopOpacity="0"/>
              </linearGradient>
            </defs>
            <path d="M0,80 C50,60 90,90 140,60 C190,30 240,70 290,45 C340,20 390,55 440,32 L500,20 L500,100 L0,100 Z" fill="url(#dashg)"/>
            <path d="M0,80 C50,60 90,90 140,60 C190,30 240,70 290,45 C340,20 390,55 440,32 L500,20" fill="none" stroke="#60a5fa" strokeWidth="1.5"/>
          </svg>
        </div>
        <div className="grid grid-cols-2 gap-2">
          <div className="rounded-xl border border-white/10 bg-white/[0.02] p-3">
            <div className="text-[10px] uppercase tracking-[0.18em] text-white/40 font-mono-x">Top Threads</div>
            {["@amelia · CRM eval","@marcus · intent scoring","@theo · outbound AI"].map(t=><div key={t} className="mt-2 text-[11px] text-white/80">{t}</div>)}
          </div>
          <div className="rounded-xl border border-white/10 bg-gradient-to-br from-[#4F46E5]/15 to-transparent p-3">
            <div className="text-[10px] uppercase tracking-[0.18em] text-[#a78bfa] font-mono-x">Today’s Focus</div>
            <div className="mt-2 text-[11px] text-white/85 leading-relaxed">Approve 12 drafts · Reply to 4 hot leads · Sync 2 accounts.</div>
          </div>
        </div>
      </div>
    </div>
  );
}

function ShotDiscovery() {
  return (
    <div className="p-5 md:p-6 min-h-[440px]">
      <div className="flex items-center gap-3 border-b border-white/10 pb-3">
        <span className="text-[10px] uppercase tracking-[0.18em] text-white/40 font-mono-x">Filter</span>
        {["Intent > 80","Founders","US/EU","Last 24h","English"].map(f=><span key={f} className="text-[10px] font-mono-x px-2 py-1 rounded-md bg-white/5 border border-white/10 text-white/70">{f}</span>)}
      </div>
      <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-3">
        {[
          {n:"Amelia Wu",r:"Founder, Northwind",s:94,q:"Any team using AI for outbound replies? Overwhelmed by DMs."},
          {n:"Marcus Delacroix",r:"Head of Growth, Octave",s:89,q:"CRM I can plug into social — recs?"},
          {n:"Theo Andriessen",r:"Ops, Meridian",s:82,q:"Intent scoring — is that a real thing?"},
          {n:"Ines Alvarez",r:"CMO, Parallel",s:78,q:"Looking for AI drafts we can approve as a team."},
        ].map((l)=>(
          <div key={l.n} className="rounded-xl border border-white/10 bg-white/[0.02] p-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <div className="h-7 w-7 rounded-full bg-white/10 text-[10px] font-mono-x flex items-center justify-center">{l.n.split(" ").map(w=>w[0]).slice(0,2).join("")}</div>
                <div>
                  <div className="text-[12px] font-medium">{l.n}</div>
                  <div className="text-[10px] text-white/50 font-mono-x">{l.r}</div>
                </div>
              </div>
              <div className="text-[10px] font-mono-x">
                <span className={`px-1.5 py-0.5 rounded ${l.s>=90 ? "bg-emerald-400/15 text-emerald-300" : l.s>=80 ? "bg-amber-400/15 text-amber-300" : "bg-white/10 text-white/70"}`}>{l.s}</span>
              </div>
            </div>
            <div className="mt-2 text-[11px] text-white/70 leading-relaxed">“{l.q}”</div>
            <div className="mt-2 flex items-center gap-2">
              <span className="text-[10px] font-mono-x px-2 py-1 rounded-md bg-white text-black">Engage</span>
              <span className="text-[10px] font-mono-x px-2 py-1 rounded-md border border-white/15 text-white/70">Save</span>
              <span className="ml-auto text-[10px] font-mono-x text-white/40">2 min ago</span>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function ShotLeadDetail() {
  return (
    <div className="p-5 md:p-6 min-h-[440px] grid grid-cols-12 gap-3">
      <div className="col-span-12 md:col-span-4">
        <div className="rounded-xl border border-white/10 bg-white/[0.02] p-4">
          <div className="flex items-center gap-3">
            <div className="h-12 w-12 rounded-full bg-white/10 flex items-center justify-center font-mono-x text-xs">AW</div>
            <div>
              <div className="font-medium">Amelia Wu</div>
              <div className="text-[11px] text-white/50 font-mono-x">Founder · Northwind</div>
            </div>
          </div>
          <div className="mt-4 grid grid-cols-2 gap-2 text-[10px] font-mono-x">
            <Info k="Intent" v="94" tone="ok"/>
            <Info k="Fit" v="A+" tone="ok"/>
            <Info k="Region" v="US-East"/>
            <Info k="TZ" v="EST"/>
          </div>
          <div className="mt-4 text-[10px] uppercase tracking-[0.18em] text-white/40 font-mono-x">Tags</div>
          <div className="mt-2 flex flex-wrap gap-1">
            {["Founder","B2B SaaS","Warm","Series A"].map(t=><span key={t} className="text-[10px] font-mono-x px-2 py-0.5 rounded-full bg-white/5 border border-white/10 text-white/70">{t}</span>)}
          </div>
        </div>
      </div>
      <div className="col-span-12 md:col-span-8 flex flex-col gap-3">
        <div className="rounded-xl border border-white/10 bg-white/[0.02] p-3">
          <div className="text-[10px] uppercase tracking-[0.18em] text-white/40 font-mono-x">Signals · past 7d</div>
          <div className="mt-2 space-y-2 text-[11px]">
            <Signal t="mentioned ‘CRM eval’" ago="2h"/>
            <Signal t="liked competitor case study" ago="1d"/>
            <Signal t="commented ‘AI for outbound?’" ago="1d"/>
            <Signal t="visited pricing page" ago="3d"/>
          </div>
        </div>
        <div className="rounded-xl border border-white/10 bg-gradient-to-br from-[#4F46E5]/12 to-transparent p-3">
          <div className="text-[10px] uppercase tracking-[0.18em] text-[#a78bfa] font-mono-x">AI recommended next action</div>
          <div className="mt-2 text-[12px] text-white/85 leading-relaxed">Send a public reply referencing her CRM eval question. Follow up in DM after 24h if no response. Confidence 91%.</div>
        </div>
      </div>
    </div>
  );
}

function Info({k,v,tone}){return(<div className="flex items-center justify-between rounded-md bg-white/[0.02] border border-white/10 px-2 py-1.5"><span className="text-white/50">{k}</span><span className={tone==="ok"?"text-emerald-300":"text-white"}>{v}</span></div>)}
function Signal({t,ago}){return(<div className="flex items-center justify-between"><span className="text-white/80">{t}</span><span className="text-white/40 font-mono-x">{ago}</span></div>)}

function ShotPipeline() {
  return (
    <div className="p-5 md:p-6 min-h-[440px] grid grid-cols-1 md:grid-cols-4 gap-3">
      {[
        {c:"Discovery",n:214,items:["Amelia W.","Marcus D.","Theo A.","Ines A."]},
        {c:"Qualified",n:87,items:["Lena K.","Owen B.","Nia R."]},
        {c:"Engaged",n:42,items:["Julian P.","Karen S."]},
        {c:"Converted",n:18,items:["Camila V."]},
      ].map((col, i)=>(
        <div key={col.c} className="rounded-xl border border-white/10 bg-white/[0.02] p-3">
          <div className="flex items-center justify-between">
            <span className="text-[11px] font-mono-x uppercase tracking-widest text-white/60">{col.c}</span>
            <span className="text-[10px] font-mono-x text-white/40">{col.n}</span>
          </div>
          <div className="mt-3 space-y-2">
            {col.items.map((it, j)=>(
              <div key={it} className="rounded-lg border border-white/10 bg-white/[0.03] p-2">
                <div className="text-[11px] text-white/85">{it}</div>
                <div className="mt-1 flex items-center gap-1">
                  <span className="text-[9px] font-mono-x text-emerald-300 px-1 py-0.5 rounded bg-emerald-400/10">high intent</span>
                  <span className="text-[9px] font-mono-x text-white/40 ml-auto">2d</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

function ShotScheduler() {
  const hours = ["09","10","11","12","13","14","15","16","17","18"];
  const days = ["Mon","Tue","Wed","Thu","Fri"];
  return (
    <div className="p-5 md:p-6 min-h-[440px]">
      <div className="flex items-center justify-between">
        <div className="text-[10px] uppercase tracking-[0.18em] text-white/40 font-mono-x">Week of Feb 3 · UTC-5</div>
        <div className="flex gap-1"><span className="text-[10px] font-mono-x px-2 py-1 rounded bg-white/5 border border-white/10 text-white/70">Day</span><span className="text-[10px] font-mono-x px-2 py-1 rounded bg-white text-black">Week</span></div>
      </div>
      <div className="mt-3 grid grid-cols-11 gap-1 text-[10px] font-mono-x">
        <div></div>
        {hours.map(h=><div key={h} className="text-white/40 text-center">{h}</div>)}
        {days.map((d,i)=>(
          <Fragment key={d}>
            <div className="text-white/40 py-2">{d}</div>
            {hours.map((h, hi)=>{
              const filled = (i===0 && (hi===2||hi===6))||(i===1 && (hi===1||hi===5||hi===8))||(i===2&&(hi===4))||(i===3&&(hi===3||hi===7))||(i===4&&hi===5);
              const kind = (i+hi)%3;
              const color = kind===0 ? "bg-[#7C3AED]/40" : kind===1 ? "bg-[#4F46E5]/40" : "bg-[#22D3EE]/40";
              return <div key={`${d}-${h}`} className={`h-6 rounded ${filled ? color : "bg-white/[0.02] border border-white/10"}`}/>;
            })}
          </Fragment>
        ))}
      </div>
      <div className="mt-4 rounded-xl border border-white/10 bg-white/[0.02] p-3">
        <div className="text-[10px] uppercase tracking-[0.18em] text-white/40 font-mono-x">Queued sends</div>
        <div className="mt-2 space-y-1 text-[11px]">
          {["10:00 · Reply to @amelia_wu","11:30 · DM @marcus.dev","14:00 · Approve batch (12)"].map(x=><div key={x} className="flex items-center justify-between"><span>{x}</span><span className="text-white/40 font-mono-x">on time</span></div>)}
        </div>
      </div>
    </div>
  );
}

function ShotAnalytics() {
  return (
    <div className="p-5 md:p-6 min-h-[440px] grid grid-cols-12 gap-3">
      <div className="col-span-12 md:col-span-8 rounded-xl border border-white/10 bg-white/[0.02] p-4">
        <div className="flex items-center justify-between">
          <div className="text-[10px] uppercase tracking-[0.18em] text-white/40 font-mono-x">Reply rate · 90d</div>
          <div className="text-[10px] font-mono-x text-emerald-300">+18.4%</div>
        </div>
        <svg viewBox="0 0 500 160" className="mt-3 w-full h-[200px]">
          {[0,40,80,120,160].map(y=><line key={y} x1="0" x2="500" y1={y} y2={y} stroke="rgba(255,255,255,0.05)"/>)}
          <path d="M0,120 C40,100 70,110 110,80 C150,50 190,90 230,60 C270,30 310,70 350,40 C390,10 430,30 500,20" fill="none" stroke="#a78bfa" strokeWidth="1.75"/>
          <path d="M0,140 C40,130 70,120 110,110 C150,100 190,110 230,90 C270,70 310,90 350,70 C390,50 430,60 500,50" fill="none" stroke="#60a5fa" strokeWidth="1.5" strokeDasharray="3 3"/>
        </svg>
        <div className="mt-2 flex items-center gap-3 text-[10px] font-mono-x">
          <span className="flex items-center gap-1"><span className="h-1.5 w-1.5 rounded-full bg-[#a78bfa]"/>This period</span>
          <span className="flex items-center gap-1"><span className="h-1.5 w-1.5 rounded-full bg-[#60a5fa]"/>Prev</span>
        </div>
      </div>
      <div className="col-span-12 md:col-span-4 space-y-3">
        {[{k:"Reply rate",v:"38.7%"},{k:"Meeting book",v:"11.4%"},{k:"Cycle time",v:"7.2d"}].map(it=>(
          <div key={it.k} className="rounded-xl border border-white/10 bg-white/[0.02] p-3">
            <div className="text-[10px] uppercase tracking-[0.18em] text-white/40 font-mono-x">{it.k}</div>
            <div className="mt-1 font-display text-2xl">{it.v}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

function ShotApproval() {
  return (
    <div className="p-5 md:p-6 min-h-[440px] space-y-2">
      {[1,2,3,4].map((i)=>(
        <div key={i} className="rounded-xl border border-white/10 bg-white/[0.02] p-3">
          <div className="flex items-start justify-between gap-3">
            <div>
              <div className="text-[11px] text-white/50 font-mono-x">Draft #{5000+i} · to @user_{i}</div>
              <div className="mt-1 text-[12px] text-white/85">Hey — noticed your thread on {["CRM eval","AI outbound","lead scoring","intent detection"][i-1]}. We just shipped X — happy to walk through in 15 min.</div>
            </div>
            <div className="flex flex-col gap-1 shrink-0">
              <span className="text-[10px] font-mono-x px-2 py-1 rounded-md bg-white text-black text-center">Approve</span>
              <span className="text-[10px] font-mono-x px-2 py-1 rounded-md border border-white/15 text-white/70 text-center">Edit</span>
            </div>
          </div>
          <div className="mt-2 flex items-center gap-2 text-[10px] font-mono-x text-white/40">
            <span>confidence 91%</span>·<span>tone matched</span>·<span>2 sec ago</span>
          </div>
        </div>
      ))}
    </div>
  );
}

function ShotTeam() {
  return (
    <div className="p-5 md:p-6 min-h-[440px]">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        {[
          {n:"Sarah Lin",r:"AE",m:{leads:214,replies:87,approved:62}},
          {n:"Owen Bello",r:"SDR",m:{leads:178,replies:72,approved:55}},
          {n:"Ines Alvarez",r:"CMO",m:{leads:91,replies:41,approved:31}},
        ].map(u=>(
          <div key={u.n} className="rounded-xl border border-white/10 bg-white/[0.02] p-3">
            <div className="flex items-center gap-2">
              <div className="h-8 w-8 rounded-full bg-white/10 text-[11px] font-mono-x flex items-center justify-center">{u.n.split(" ").map(w=>w[0]).join("")}</div>
              <div>
                <div className="text-[12px] font-medium">{u.n}</div>
                <div className="text-[10px] font-mono-x text-white/40">{u.r}</div>
              </div>
            </div>
            <div className="mt-3 grid grid-cols-3 gap-2 text-[10px] font-mono-x">
              <div><div className="text-white/40">Leads</div><div className="text-white">{u.m.leads}</div></div>
              <div><div className="text-white/40">Replies</div><div className="text-white">{u.m.replies}</div></div>
              <div><div className="text-white/40">Approved</div><div className="text-white">{u.m.approved}</div></div>
            </div>
          </div>
        ))}
      </div>
      <div className="mt-4 rounded-xl border border-white/10 bg-white/[0.02] p-3">
        <div className="text-[10px] uppercase tracking-[0.18em] text-white/40 font-mono-x">Live activity</div>
        <div className="mt-2 space-y-1 text-[11px]">
          {[
            "Sarah approved 4 drafts",
            "Owen engaged with @marcus.dev",
            "Ines added @amelia_wu to Qualified",
          ].map((x,i)=><div key={i} className="flex items-center justify-between"><span>{x}</span><span className="text-white/40 font-mono-x">just now</span></div>)}
        </div>
      </div>
    </div>
  );
}
